import {
  loadPreferences,
  savePreferences,
  saveRunningPace,
  type SavePreferencesInput,
  type SerializedPreferences
} from "./preferencesApi";
import {
  getAnonPreferencesDraft,
  getAnonRunningPaceDraft,
  setAnonPreferencesDraft,
  setAnonRunningPaceDraft
} from "./anonDraft";
import { isValidRunningPaceSecPerMi } from "@shared/modules/preferences/runningPace";
import {
  getAnonPhysiologySession,
  setAnonPhysiologySession,
  loadProfile,
  saveProfile,
  type SaveProfileInput
} from "./profileApi";
import { notifyPreferencesSaved } from "./preferencesSaved";

/**
 * Anon draft → account promotion (docs/plans/preferences-outbound-alignment.md,
 * Slice A). Guests hold preferences and physiology in localStorage drafts; on
 * sign-in those must land on the account's server rows or every box generation
 * dispatches with empty preferences / an all-null athlete block while the UI
 * scores against the draft.
 *
 * Merge philosophy mirrors the server's FuelingProfile anon-merge: the ACCOUNT
 * wins; the draft only fills blanks. Love-vs-avoid conflicts and hidden-key
 * restoration are the server's job (the v3 POST already does both) — the client
 * merge stays dumb.
 *
 * Fail-open by design: any error leaves the draft in place for a retry on the
 * next mount. Drafts that contribute nothing are cleared so promotion never
 * re-runs against stale empties.
 */

export type PromotionSummary = { preferences: boolean; physiology: boolean };

type ListKey =
  | "dietaryNeeds"
  | "preferredProductTypes"
  | "excludedProductTypes"
  | "preferredFlavorProfiles"
  | "excludedFlavorProfiles"
  | "preferredBrands"
  | "excludedBrands";

const LIST_KEYS: ListKey[] = [
  "dietaryNeeds",
  "preferredProductTypes",
  "excludedProductTypes",
  "preferredFlavorProfiles",
  "excludedFlavorProfiles",
  "preferredBrands",
  "excludedBrands"
];

function draftList(draft: Partial<SavePreferencesInput>, key: ListKey): string[] {
  const v = draft[key];
  return Array.isArray(v) ? v.filter((s) => typeof s === "string" && s.length > 0) : [];
}

/** True when the draft carries anything a save would persist. */
function draftHasContent(draft: Partial<SavePreferencesInput>): boolean {
  if (LIST_KEYS.some((k) => draftList(draft, k).length > 0)) return true;
  if (draft.caffeineAllowed === false) return true;
  if (draft.caffeineMaxMgPerHour != null) return true;
  if (typeof draft.caffeineMaxPercentOfFuel === "number" && draft.caffeineMaxPercentOfFuel !== 50)
    return true;
  return false;
}

/** Full save body from a (possibly partial) draft — about-you writes partial drafts. */
function bodyFromDraft(draft: Partial<SavePreferencesInput>): SavePreferencesInput {
  return {
    dietaryNeeds: draftList(draft, "dietaryNeeds"),
    // Removed from the product; the POST route ignores the body value and
    // carries the stored row through regardless.
    dietaryPreferences: [],
    preferredProductTypes: draftList(draft, "preferredProductTypes"),
    excludedProductTypes: draftList(draft, "excludedProductTypes"),
    preferredFlavorProfiles: draftList(draft, "preferredFlavorProfiles"),
    excludedFlavorProfiles: draftList(draft, "excludedFlavorProfiles"),
    preferredBrands: draftList(draft, "preferredBrands"),
    excludedBrands: draftList(draft, "excludedBrands"),
    caffeineAllowed: draft.caffeineAllowed ?? true,
    caffeineMaxMgPerHour: draft.caffeineMaxMgPerHour ?? null,
    caffeineMaxPercentOfFuel:
      typeof draft.caffeineMaxPercentOfFuel === "number" ? draft.caffeineMaxPercentOfFuel : 50
  };
}

/**
 * Gap-fill an existing account row from the draft: per list, the draft is used
 * only where the server list is empty. Caffeine is NEVER promoted onto an
 * existing row — the serialized shape's defaults (`allowed: true`, `50%`) are
 * indistinguishable from deliberate choices, so the row's values are treated as
 * authoritative. Returns null when the draft fills no gaps (nothing to save).
 */
function gapFillBody(
  server: SerializedPreferences,
  draft: Partial<SavePreferencesInput>
): SavePreferencesInput | null {
  let filled = false;
  const pick = (key: ListKey): string[] => {
    if (server[key].length > 0) return server[key];
    const fromDraft = draftList(draft, key);
    if (fromDraft.length > 0) filled = true;
    return fromDraft;
  };
  const body: SavePreferencesInput = {
    dietaryNeeds: pick("dietaryNeeds"),
    dietaryPreferences: [],
    preferredProductTypes: pick("preferredProductTypes"),
    excludedProductTypes: pick("excludedProductTypes"),
    preferredFlavorProfiles: pick("preferredFlavorProfiles"),
    excludedFlavorProfiles: pick("excludedFlavorProfiles"),
    preferredBrands: pick("preferredBrands"),
    excludedBrands: pick("excludedBrands"),
    caffeineAllowed: server.caffeineAllowed,
    caffeineMaxMgPerHour: server.caffeineMaxMgPerHour,
    caffeineMaxPercentOfFuel: server.caffeineMaxPercentOfFuel
  };
  return filled ? body : null;
}

/** Cheap sync check so the auth bootstrap can skip promotion entirely (common case). */
export function hasAnonDraftsToPromote(): boolean {
  return (
    getAnonPreferencesDraft<Partial<SavePreferencesInput>>() != null ||
    getAnonPhysiologySession() != null ||
    getAnonRunningPaceDraft() != null
  );
}

async function promotePreferences(
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<boolean> {
  const draft = getAnonPreferencesDraft<Partial<SavePreferencesInput>>();
  if (!draft) return false;
  if (!draftHasContent(draft)) {
    setAnonPreferencesDraft(null);
    return false;
  }
  const res = await loadPreferences();
  const server = res.preferences;
  // `exists === false` → no row: promote wholesale. `exists` true or absent
  // (older server) → gap-fill; the account row wins where non-empty.
  const body =
    server == null || server.exists === false
      ? bodyFromDraft(draft)
      : gapFillBody(server, draft);
  if (!body) {
    // Draft fills no gaps — it contributed nothing; clear so this never re-runs.
    setAnonPreferencesDraft(null);
    return false;
  }
  await savePreferences(body, csrfToken, refreshCsrf);
  setAnonPreferencesDraft(null);
  notifyPreferencesSaved();
  return true;
}

async function promoteRunningPace(
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<void> {
  const draft = getAnonRunningPaceDraft();
  if (draft == null || !isValidRunningPaceSecPerMi(draft)) {
    if (draft != null) setAnonRunningPaceDraft(null);
    return;
  }
  const res = await loadPreferences();
  const serverPace = res.preferences?.averageRunningPaceSecondsPerMile ?? null;
  if (serverPace != null && isValidRunningPaceSecPerMi(serverPace)) {
    setAnonRunningPaceDraft(null);
    return;
  }
  await saveRunningPace(draft, csrfToken, refreshCsrf);
  setAnonRunningPaceDraft(null);
}

async function promotePhysiology(
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<boolean> {
  const draft = getAnonPhysiologySession();
  if (!draft) return false;
  if (draft.sex == null && draft.weight == null) {
    setAnonPhysiologySession(null);
    return false;
  }
  // Force-read: the process-lifetime profile cache may hold a pre-login shape.
  const profile = await loadProfile(undefined, { force: true });
  const patch: SaveProfileInput = {};
  if (profile.sex == null && draft.sex != null) patch.sex = draft.sex;
  if (profile.weight == null && draft.weight != null) {
    patch.weight = draft.weight;
    patch.weightUnit = draft.weightUnit ?? "lb";
  }
  if (Object.keys(patch).length === 0) {
    setAnonPhysiologySession(null);
    return false;
  }
  await saveProfile(patch, csrfToken, refreshCsrf);
  setAnonPhysiologySession(null);
  return true;
}

/**
 * Run both promotions. Each is independently fail-open: an error leaves THAT
 * draft in place (retried next mount) and never blocks the other.
 */
export async function promoteAnonDrafts(opts: {
  csrfToken: string;
  refreshCsrf: () => Promise<string>;
}): Promise<PromotionSummary> {
  const summary: PromotionSummary = { preferences: false, physiology: false };
  try {
    summary.preferences = await promotePreferences(opts.csrfToken, opts.refreshCsrf);
  } catch {
    // Draft retained; retry on next mount.
  }
  try {
    summary.physiology = await promotePhysiology(opts.csrfToken, opts.refreshCsrf);
  } catch {
    // Draft retained; retry on next mount.
  }
  try {
    await promoteRunningPace(opts.csrfToken, opts.refreshCsrf);
  } catch {
    // Draft retained; retry on next mount.
  }
  return summary;
}

/** A′ bootstrap timeout — a slow promotion must never wedge the loading gate. */
export const PROMOTION_TIMEOUT_MS = 3000;

/**
 * Bounded promotion for the auth bootstrap. Resolves `null` on timeout — the
 * app proceeds, drafts stay put, and the in-flight requests settle harmlessly
 * in the background (a late save still clears its draft).
 */
export async function promoteAnonDraftsWithTimeout(opts: {
  csrfToken: string;
  refreshCsrf: () => Promise<string>;
}): Promise<PromotionSummary | null> {
  return Promise.race([
    promoteAnonDrafts(opts),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), PROMOTION_TIMEOUT_MS))
  ]);
}
